import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { PartnerSafeApi } from './EnvFiles';
import { getStoredToken } from '../utils/getStoredToken';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { sortImages } from '../utils/sortImages';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.mode === 'light'
      ? '#FFFFFF'
      : 'linear-gradient(145deg, #121212 0%, #1a1a1a 100%)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
      : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
    border: theme.palette.mode === 'light'
      ? '1px solid rgba(0, 0, 0, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${
    theme.palette.primary.main
  } 30%, ${
    theme.palette.primary.light
  } 90%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '180px',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #e0e0e0',
  boxShadow: 'none',
  transition: 'border 0.2s, box-shadow 0.2s',
  '&:hover': {
    border: '2px solid #66FF00',
    boxShadow: '0 0 0 3px #66FF0044',
  },
  '&:hover img': {
    filter: 'brightness(1.08) drop-shadow(0 2px 12px rgba(0,0,0,0.18))',
    transform: 'scale(1.03)',
    transition: 'all 0.2s',
  },
}));

const Overlay = styled('div')(({ theme, status, $show }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  pointerEvents: 'none',
  background:
    status === 'approve'
      ? 'rgba(102, 255, 0, 0.35)'
      : status === 'reject' && $show
      ? 'rgba(220, 38, 38, 0.35)'
      : 'transparent',
  transition: 'background 0.3s, opacity 0.3s',
  zIndex: 1,
}));

const ImageActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  background: 'transparent',
  zIndex: 2,
  padding: 0,
}));

const ActionButton = styled(IconButton)(({ theme, actiontype, selected }) => ({
  color: 'white',
  backgroundColor: actiontype === 'approve' ? '#66FF00' : '#DC2626',
  border: selected ? '2px solid #fff' : 'none',
  transform: selected ? 'scale(1.15)' : 'scale(1)',
  transition: 'all 0.2s',
  opacity: selected ? 1 : 0.7,
  '&:hover': {
    opacity: 1,
  },
}));

const ImageLabel = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  background: 'rgba(0,0,0,0.7)',
  color: '#fff',
  padding: '2px 8px',
  borderRadius: '6px',
  fontWeight: 500,
  fontSize: 13,
  zIndex: 3,
  pointerEvents: 'none',
}));

const ZoomIconOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'rgba(0,0,0,0.55)',
  borderRadius: '50%',
  padding: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s',
  zIndex: 4,
  pointerEvents: 'none',
}));

const ReasonInfoIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 5,
  color: '#fff',
  background: 'rgba(220,38,38,0.85)',
  borderRadius: '50%',
  padding: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const ReviewDialog = ({
  open,
  onClose,
  isImagesLoading,
  selectedImages,
  onOpenZoom,
  imageStatuses = {},
  rejectionReasons = {},
  openRejectionDialog,
  currentRejectValue,
  handleImageAction,
  handleRejectionReasonCancel,
  handleChangeCurrentRejectValue,
  handleRejectionReasonConfirm,
  handleUpdateTableData,
  canReview,
  handleOpenRejectionReasonDialog,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Adicionar log para verificar as imagens
  React.useEffect(() => {
    if (selectedImages?.length) {
      console.log('Total de imagens carregadas:', selectedImages.length);
      console.log('Detalhes completos das imagens:', selectedImages);
      
      // Verificar se há imagens duplicadas
      const tiposUnicos = new Set(selectedImages.map(img => `${img.tipoImagem}-${img.imagemIndice}`));
      console.log('Tipos únicos de imagens:', tiposUnicos);
      
      if (tiposUnicos.size !== selectedImages.length) {
        console.warn('Possível duplicação de imagens detectada!');
      }
    }
  }, [selectedImages]);

  // Ordenar as imagens antes de renderizar
  const imagensOrdenadas = useMemo(() => (
    selectedImages ? sortImages(selectedImages) : []
  ), [selectedImages]);

  const isAllEvaluated = useMemo(() => (
    imagensOrdenadas.length > 0 && imagensOrdenadas.every((img) => {
      if (imageStatuses[img.identificador] === 'reject') {
        return !!rejectionReasons[img.identificador];
      }

      return !!imageStatuses[img.identificador];
    })
  ), [imagensOrdenadas, imageStatuses, rejectionReasons]);

  const handleConfirm = async () => {
    setIsSubmitting(true);

    const token = getStoredToken();

    try {
      const promises = imagensOrdenadas.map((img) => {
        const imagemId = img.identificador;
        const status = imageStatuses[imagemId];

        if (status === 'approve') {
          return axios.post(
            `${PartnerSafeApi}/api/v1/Processos6Ros/AprovarImagem6Ro`,
            { imagem6RoIdentificador: imagemId },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else if (status === 'reject') {
          return axios.post(
            `${PartnerSafeApi}/api/v1/Processos6Ros/RejeitarImagem6Ro`,
            {
              imagem6RoIdentificador: imagemId,
              motivoDaReprovacao: rejectionReasons[imagemId],
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }

        return null;
      });

      await Promise.all(promises.filter(Boolean));

      handleUpdateTableData();
      setSnackbar({
        open: true,
        message: 'Imagens processadas com sucesso!',
        severity: 'success',
      });
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao processar imagens. Tente novamente.',
        severity: 'error',
      });
      console.error('Erro ao processar imagens:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <GradientText variant="h6">Revisar Registro</GradientText>
        <IconButton
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isImagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
            <Grid container spacing={2} sx={{ minWidth: { xs: 600, md: 'unset' } }}>
              {imagensOrdenadas.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={image.identificador}>
                  <ImageContainer status={imageStatuses[image.identificador]}>
                    <ImageLabel variant="caption">
                      {image.tipoImagem} - Imagem {image.imagemIndice}
                    </ImageLabel>
                    {imageStatuses[image.identificador] === 'reject' && !!rejectionReasons[image.identificador] && (
                      <Tooltip title={rejectionReasons[image.identificador]} arrow>
                        <ReasonInfoIcon>
                          <InfoOutlinedIcon fontSize="small" />
                        </ReasonInfoIcon>
                      </Tooltip>
                    )}
                    <Overlay
                      status={imageStatuses[image.identificador]}
                      $show={!!rejectionReasons[image.identificador]}
                    />
                    <ZoomIconOverlay className="zoom-icon-overlay" aria-label="Ampliar imagem">
                      <ZoomInIcon sx={{ color: '#fff', fontSize: 32 }} />
                    </ZoomIconOverlay>
                    <img
                      src={image.fotoLink}
                      alt={`${image.tipoImagem} - Imagem ${image.imagemIndice}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        background: '#f8f8f8',
                        position: 'relative',
                        cursor: 'zoom-in',
                      }}
                      tabIndex={0}
                      aria-label="Clique para ampliar a imagem"
                      onClick={() => onOpenZoom(image, true)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpenZoom(image, true); }}
                    />
                    {canReview && (
                      <ImageActions className="image-actions">
                        <Tooltip title="Aprovar esta imagem" arrow>
                          <span>
                            <ActionButton
                              size="small"
                              actiontype="approve"
                              selected={imageStatuses[image.identificador] === 'approve'}
                              onClick={() => handleImageAction(image.identificador, 'approve')}
                              disabled={isSubmitting}
                              aria-label="Aprovar imagem"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </ActionButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rejeitar esta imagem" arrow>
                          <span>
                            <ActionButton
                              size="small"
                              actiontype="reject"
                              selected={imageStatuses[image.identificador] === 'reject'}
                              onClick={() => handleImageAction(image.identificador, 'reject')}
                              disabled={isSubmitting}
                              aria-label="Rejeitar imagem"
                            >
                              <CancelIcon fontSize="small" />
                            </ActionButton>
                          </span>
                        </Tooltip>
                      </ImageActions>
                    )}
                  </ImageContainer>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      {canReview && (
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            sx={{
              background: (theme) => (
                `linear-gradient(45deg, ${
                  theme.palette.error.main
                } 30%, ${
                  theme.palette.error.light
                } 90%)`
              ),
              boxShadow: (theme) => (
                `0 4px 14px 0 ${theme.palette.error.main}40`
              ),
              '&:hover': {
                boxShadow: (theme) => (
                  `0 6px 20px 0 ${theme.palette.error.main}60`
                ),
              },
            }}
            startIcon={isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CancelIcon />
            )}
            onClick={handleOpenRejectionReasonDialog}
          >
            Rejeitar registro
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            startIcon={isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )}
            disabled={isSubmitting || !isAllEvaluated}
            sx={{
              background: (theme) => (
                `linear-gradient(45deg, ${
                  theme.palette.success.main
                } 30%, ${
                  theme.palette.success.light
                } 90%)`
              ),
              boxShadow: (theme) => (
                `0 4px 14px 0 ${theme.palette.success.main}40`
              ),
              '&:hover': {
                boxShadow: (theme) => (
                  `0 6px 20px 0 ${theme.palette.success.main}60`
                ),
              },
            }}
          >
            {isSubmitting ? 'Aguarde' : 'Salvar'}
          </Button>
        </DialogActions>
      )}
      {/* Dialog para motivo da rejeição */}
      <Dialog open={openRejectionDialog} onClose={handleRejectionReasonCancel}>
        <DialogTitle>Motivo da Rejeição</DialogTitle>
        <DialogContent>
          <DialogContentText>Por favor, informe o motivo da rejeição desta imagem.</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Motivo da Rejeição"
            type="text"
            fullWidth
            value={currentRejectValue}
            onChange={e => handleChangeCurrentRejectValue(e.target.value)}
            multiline
            minRows={2}
            error={!!currentRejectValue && currentRejectValue.trim().length < 15}
            helperText={
              !!currentRejectValue && currentRejectValue.trim().length < 15
                ? 'O motivo deve ter pelo menos 15 caracteres.'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectionReasonCancel}>Cancelar</Button>
          <Button onClick={handleRejectionReasonConfirm} disabled={currentRejectValue.trim().length < 15} variant="contained" color="error">Confirmar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </StyledDialog>
  );
};

export default ReviewDialog;